/* eslint-disable @typescript-eslint/no-unused-vars */
/*
 *   Copyright (c) 2025 2023 Celadon Development LLC
 *   All rights reserved.
 *   Author Aliaksandr Ziabko <aliaksandr.ziabko@celadonsoft.com>
 */
import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Header from "../../components/Header";
import BackButton from "../../components/BackButton";
import UploadButton from "../../components/UploadButton";
import { useNavigate } from "react-router";
import SuccessMessage from "../../components/SuccessMessage";
import ErrorMessage from "../../components/ErrorMessage";
import { createTrainer, updateTrainer } from "../../services/trainerService";

interface TrainerFormValues {
  name: string;
  qualification: string;
  experience: string;
  description: string;
  instagramUrl: string;
  tiktokUrl: string;
  photo?: File | null;
}

const TrainerValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Name is required")
    .matches(/^(?!\s*$).+/, "Name cannot be empty or just spaces"),
  qualification: Yup.string()
    .required("Qualification is required")
    .matches(/^(?!\s*$).+/, "Qualification cannot be empty or just spaces"),
  experience: Yup.string()
    .required("Experience is required")
    .matches(/^(?!\s*$).+/, "Experience cannot be empty or just spaces"),
});

interface AddTrainerProps {
  trainerId?: number; // Если редактирование
}

const AddTrainer: React.FC<AddTrainerProps> = ({ trainerId }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [, setPhotoPreview] = useState<string | null>(null);

  const initialValues: TrainerFormValues = {
    name: "",
    qualification: "",
    experience: "",
    description: "",
    instagramUrl: "",
    tiktokUrl: "",
    photo: null,
  };

  const handleSubmit = async (values: TrainerFormValues) => {
    try {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("description", values.description);
      formData.append("experience", values.experience);
      formData.append("qualification", values.qualification);
      if (values.instagramUrl) formData.append("instagramUrl", values.instagramUrl);
      if (values.tiktokUrl) formData.append("tiktokUrl", values.tiktokUrl);
      if (values.photo) formData.append("photo", values.photo);

      if (trainerId) {
        await updateTrainer(trainerId, formData);
        setSuccessMessage("Trainer updated successfully!");
      } else {
        await createTrainer(formData);
        setSuccessMessage("Trainer added successfully!");
      }

      setTimeout(() => navigate("/trainers"), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save trainer");
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <Header title={trainerId ? "Edit Trainer" : "Add Trainer"} />
      <div className="w-full max-w-lg bg-white shadow-md rounded-md p-6">
        {error && (
          <ErrorMessage message={error} onClose={() => setError(null)} />
        )}
        {successMessage && (
          <SuccessMessage
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
          />
        )}

        <div className="flex items-center justify-between mt-4 border-b pb-4">
          <BackButton onClick={() => navigate("/trainers")} />
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={TrainerValidationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, setFieldValue, values, dirty }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Name
                </label>
                <Field
                  name="name"
                  type="text"
                  placeholder="Trainer Name"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.name && touched.name && (
                  <div className="text-red-500 text-xs mt-1">{errors.name}</div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Qualification
                </label>
                <Field
                  name="qualification"
                  type="text"
                  placeholder="Trainer Qualification"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.qualification && touched.qualification && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.qualification}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Experience
                </label>
                <Field
                  name="experience"
                  type="text"
                  placeholder="Trainer Experience"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.experience && touched.experience && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.experience}
                  </div>
                )}
              </div>
              <div>
                <label>Description</label>
                <Field
                  name="description"
                  type="text"
                  placeholder="Description"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
                {errors.description && touched.description && (
                  <div className="text-red-500">{errors.description}</div>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Instagram URL
                </label>
                <Field
                  name="instagramUrl"
                  type="text"
                  placeholder="https://instagram.com/username"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  TikTok URL
                </label>
                <Field
                  name="tiktokUrl"
                  type="text"
                  placeholder="https://tiktok.com/@username"
                  className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Photo
                </label>
                <UploadButton
                  label="Upload Photo"
                  accept="image/*"
                  onUpload={async (file: File | null, index: number) => {
                    if (!file) return null;
                    setFieldValue("photo", file);
                    setPhotoPreview(URL.createObjectURL(file));
                    return URL.createObjectURL(file); // или возвращай URL после загрузки через сервис
                  }}
                  onRemove={() => {
                    setFieldValue("photo", null);
                    setPhotoPreview(null);
                  }}
                  index={0}
                />
              </div>

              <button
                type="submit"
                disabled={!dirty}
                className={`w-full py-2 text-white font-semibold rounded-md transition ${
                  dirty
                    ? "bg-yellow-600 hover:bg-yellow-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                {trainerId ? "Update Trainer" : "Add Trainer"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AddTrainer;
